import React from 'react'
import Blob from '@/components/Blob'
import Illustration from '@/components/Illustration'
import Card from '@/components/ui/Card'

const values = [
  {
    title: 'Transparency',
    description:
      'We keep sponsors, teachers, and families informed every step of the journey with real-time updates and open reporting.',
    illustration: 'leaf',
  },
  {
    title: 'Equity',
    description:
      'We center classrooms that are historically under-resourced so every student can access joyful, enriching learning.',
    illustration: 'kids',
  },
  {
    title: 'Partnership',
    description:
      'We build long-term relationships between educators, community partners, and donors to amplify collective impact.',
    illustration: 'teacher',
  },
]

const impactStats = [
  { label: 'Classes Sponsored', value: '2' },
  { label: 'Total Sponsors', value: '1' },
  { label: 'Total Funding', value: '$1,600' },
]

// Requested team
const teamMembers = [
  { name: 'Mycal Brooks',      role: 'President' },
  { name: 'Renee Brooks',      role: 'Vice President' },
  { name: 'Mycah Brooks',      role: 'Community Outreach Lead' },
  { name: 'Bella-Rose Brooks', role: 'Art & Entertainment' },
]

export default function About() {
  return (
    <div className="space-y-12">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-shell via-white to-shell px-6 py-12 shadow-soft">
        <Blob className="absolute -top-24 -left-16 h-64 w-64 opacity-50" />
        <Blob className="absolute -bottom-24 -right-12 h-60 w-60 opacity-40" />
        <div className="relative z-10 grid gap-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] md:items-center">
          <div className="space-y-4">
            <span className="inline-flex items-center rounded-full bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa/70">
              About
            </span>
            <h1 className="text-4xl font-semibold tracking-tight text-cocoa">About Project Tower</h1>
            <p className="text-lg leading-relaxed text-cocoa/80">
              We elevate classrooms by pairing imaginative educators with the sponsors and partners who can help them soar.
              Through thoughtful stewardship, transparent reporting, and joyful storytelling, we remove the barriers between
              bold ideas and the resources they need.
            </p>
          </div>
          <Illustration name="books" animate className="mx-auto h-40 w-40 md:h-56 md:w-56" />
        </div>
      </section>

      <Card className="relative overflow-hidden">
        <Blob className="absolute -top-20 -right-20 h-52 w-52 opacity-30" />
        <div className="relative z-10 grid gap-10 md:grid-cols-2 md:items-center">
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold text-cocoa">Our Story</h2>
            <p className="text-cocoa/80">
              Project Tower was born from the belief that every student deserves the chance to dream audaciously. We started as a
              volunteer collective, helping teachers source supplies and experiences for their classrooms. Today, we connect
              classrooms across the region with sponsors who invest in hands-on learning, mentorship, and community experiences.
            </p>
            <p className="text-cocoa/80">
              By combining technology with human relationships, we make it easy for supporters to see their impact and for
              educators to focus on what matters most—sparking curiosity and confidence in their students.
            </p>
          </div>
          <div className="flex justify-center">
            <Illustration name="teacher" className="h-52 w-52" animate />
          </div>
        </div>
      </Card>

      <section className="grid gap-6 md:grid-cols-3">
        {values.map((value) => (
          <Card key={value.title} className="relative overflow-hidden">
            <Blob className="absolute -top-16 -left-16 h-40 w-40 opacity-20" />
            <div className="relative z-10 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-cocoa">{value.title}</h3>
                <Illustration name={value.illustration} className="h-12 w-12" animate />
              </div>
              <p className="text-sm leading-relaxed text-cocoa/80">{value.description}</p>
            </div>
          </Card>
        ))}
      </section>

      <Card>
        <div className="grid gap-6 text-center md:grid-cols-3">
          {impactStats.map((stat) => (
            <div key={stat.label} className="space-y-1">
              <div className="text-3xl font-semibold text-cocoa">{stat.value}</div>
              <div className="text-sm uppercase tracking-wide text-cocoa/60">{stat.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="space-y-6">
          <header className="space-y-2 text-center">
            <h2 className="text-3xl font-semibold text-cocoa">Meet the Team</h2>
            <p className="text-sm text-cocoa/70">
              A dedicated group of educators, technologists, and community builders uplifting classrooms together.
            </p>
          </header>
          <div className="grid gap-4 sm:grid-cols-2">
            {teamMembers.map((member) => (
              <div key={member.name} className="rounded-2xl border border-white/60 bg-white/70 p-4 text-center shadow-inner">
                <div className="text-lg font-semibold text-cocoa">{member.name}</div>
                <div className="text-sm text-cocoa/70">{member.role}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  )
}
